import { create } from "zustand";

/**
 * Real-time MQTT hodnoty z WS streamu (živý "current value" per topic). Po refactoru
 * 2026-05 (CHART-DATA-REFACTOR Fáze 7) drží jen `byTopic` (latest) — chartová historie
 * je separátně v `useChartStore` (tile-based cache s server-side bucketing).
 */
interface ValuesStore {
  /** O(1) lookup — poslední hodnota per topic. */
  byTopic: Map<string, MQTTValue>;
  /** Přijetí WS batche — parsuje timestampy + ukládá do `byTopic`. */
  applyWsBatch: (batch: MQTTValueTransfer[]) => void;
  /**
   * "Tichý heartbeat" — hodnota se nezměnila, ale stále teče. Pro každý topicId
   * vytvoří novou referenci `MQTTValue` se stávající `value` a novým `timestamp`.
   * Tím se v `useTopic` přepočítá `lastValueInterval` a Dot animace restartuje.
   */
  applyHeartbeat: (topicIds: string[], ts: number) => void;
  /** Reset (logout). */
  clear: () => void;
}

const parseTransfer = (val: MQTTValueTransfer): MQTTValue =>
  ({
    ...val,
    timestamp: new Date(val.timestamp),
    prev: val.prev
      ? {
          ...val.prev,
          timestamp: new Date(val.prev.timestamp),
          prev: val.prev.prev
            ? {
                ...val.prev.prev,
                timestamp: new Date(val.prev.prev.timestamp),
              }
            : undefined,
        }
      : undefined,
  }) as MQTTValue;

export const useValuesStore = create<ValuesStore>((set) => ({
  byTopic: new Map(),

  applyWsBatch: (batch) =>
    set((state) => {
      const newByTopic = new Map(state.byTopic);
      for (const transferVal of batch) {
        const val = parseTransfer(transferVal);
        const prevStored = newByTopic.get(val.topicId);
        if (!val.topic) {
          // Topic metadata může chybět — merguj z předchozího známého
          if (prevStored) val.topic = prevStored.topic;
          else continue; // unknown topic, drop
        }
        // Server v live WS update neposílá `prev` (jen v initial dumpu) — doplníme
        // z dosud uložené hodnoty, jinak useTopic nedopočítá interval a Dot nefunguje.
        // Drop prev.prev: klienti čtou jen jednu úroveň, hlubší řetěz by rostl donekonečna.
        if (!val.prev && prevStored) {
          val.prev = { ...prevStored, prev: undefined };
        }
        newByTopic.set(val.topicId, val);
      }
      return { byTopic: newByTopic };
    }),

  applyHeartbeat: (topicIds, ts) =>
    set((state) => {
      if (topicIds.length === 0) return state;
      const newByTopic = new Map(state.byTopic);
      const newTs = new Date(ts);
      let changed = false;
      for (const topicId of topicIds) {
        const prev = newByTopic.get(topicId);
        // Neznámý topic (např. server posílá heartbeat dřív, než dorazila plná hodnota) →
        // ignorovat. Jakmile přijde plná hodnota přes `values`, heartbeaty se začnou aplikovat.
        if (!prev) continue;
        // Nová reference se stávající `value`, `topic`, `prev` chainem, ale novým timestampem.
        // useMemo v useTopic se přepočítá → animationDuration restartuje Dot animaci.
        newByTopic.set(topicId, {
          ...prev,
          timestamp: newTs,
          prev: { ...prev, prev: undefined },
        });
        changed = true;
      }
      return changed ? { byTopic: newByTopic } : state;
    }),

  clear: () => set({ byTopic: new Map() }),
}));
